import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import customEventBus from '@common/scripts/libs/eventBus';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { debounce } from 'lodash';
import cookie from 'react-cookies';
import useMediaQueries from './useMediaQueries';
gsap.registerPlugin(ScrollTrigger);
const markers = cookie.load('gsap_markers') === 'header';
function useHeaderRef(isPdp) {
    const ref = useRef(null);
    const scrollOffset = useRef(0);
    const lastScroll = useRef(0);
    const { isReflow } = useMediaQueries();
    const [height, setHeight] = useState(0);
    const [hero, setHero] = useState(null);
    const [animable, setAnimable] = useState(!isPdp);
    // ANCHOR - OBSERVER IS PDP -------------------------------------------------
    useEffect(() => {
        customEventBus.on('header:setpdp', (data) => {
            setAnimable(!data.isPdp);
            if (data.isPdp) {
                headerAnimation(0);
                customEventBus.emit('header:settransparency', {
                    transparent: false,
                    color: 'black',
                });
            }
        });
        customEventBus.on('header:reset', () => headerAnimation(0));
        return () => {
            customEventBus.off('header:setpdp', () => { });
            customEventBus.off('header:reset', () => headerAnimation(0));
        };
    }, []);
    useEffect(() => {
        const toggleAnimation = (data) => {
            scrollOffset.current = height;
            setAnimable((prev) => (!!data.hardSet ? data.hardSet : !prev));
        };
        customEventBus.on('header:toggleanimation', toggleAnimation);
        return () => customEventBus.off('header:toggleanimation', toggleAnimation);
    }, [height]);
    const root = document.documentElement;
    // ANCHOR - OBSERVER HEIGHT -------------------------------------------------
    useEffect(() => {
        if (!ref.current)
            return;
        const observer = new ResizeObserver((entries) => {
            var _a;
            const entry = entries[0];
            if (entry) {
                let h;
                if (((_a = entry.borderBoxSize) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                    h = entry.borderBoxSize[0].blockSize;
                }
                else {
                    h = entry.contentRect.height;
                }
                root.style.setProperty('--header-h', h + 'px');
                customEventBus.emit('header:setheight', { height: h });
                setHeight(h);
            }
        });
        observer.observe(ref.current);
        return () => {
            observer.disconnect();
        };
    }, []);
    // ANCHOR - OBSERVER MAIN ------------------------------------------------
    useLayoutEffect(() => {
        const main = document.getElementsByTagName('main')[0];
        if (main) {
            const observer = new ResizeObserver(debounce(() => {
                const scrollerInPage = ScrollTrigger.getAll();
                if (!!(scrollerInPage === null || scrollerInPage === void 0 ? void 0 : scrollerInPage.length)) {
                    if (markers) {
                        console.log('ScrollTrigger updated', scrollerInPage.map((el) => { var _a; return ((_a = el === null || el === void 0 ? void 0 : el.vars) === null || _a === void 0 ? void 0 : _a.id) || (el === null || el === void 0 ? void 0 : el.trigger) || el; }));
                    }
                    scrollerInPage.forEach((el) => el.refresh());
                }
                const _hero = document.querySelector('[data-hero-scroll="true"]');
                if (_hero) {
                    setHero(_hero);
                }
                else {
                    setHero(null);
                }
            }, 200));
            observer.observe(main);
            return () => {
                setHero(null);
                observer.disconnect();
            };
        }
    }, []);
    // ANCHOR - SCROLL ANIMATION -------------------------------------------------
    const headerAnimation = useCallback((offset) => {
        gsap.set(ref.current.parentElement, {
            y: -offset,
        });
        customEventBus.emit('header:onprogress', { scroll: -offset, height });
        // setHeaderOffset(offset);
        scrollOffset.current = offset;
        root.style.setProperty('--header-scroll', offset + 'px');
    }, [height]);
    useLayoutEffect(() => {
        var _a;
        if (!isReflow) {
            let _headerOffset;
            const handleScroll = () => {
                const scrollY = window.scrollY;
                const scrollDifference = scrollY - lastScroll.current;
                if (scrollY > 0) {
                    if (scrollDifference > 0) {
                        // Scroll down: nascondi l'header gradualmente
                        _headerOffset = Math.min(scrollOffset.current + scrollDifference, height);
                    }
                    else {
                        // Scroll up: mostra l'header gradualmente
                        _headerOffset = Math.max(scrollOffset.current + scrollDifference, 0);
                    }
                    if (_headerOffset !== scrollOffset.current && animable) {
                        headerAnimation(_headerOffset);
                        root.style.setProperty('--header-scroll', _headerOffset + 'px');
                    }
                }
                else {
                    headerAnimation(0);
                }
                lastScroll.current = scrollY;
            };
            window.addEventListener('scroll', handleScroll);
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        }
        else {
            gsap.set((_a = ref.current) === null || _a === void 0 ? void 0 : _a.parentElement, {
                y: 0,
            });
        }
    }, [height, animable, isReflow]);
    useEffect(() => {
        customEventBus.on('header:resetprogress', () => {
            const diff = window.scrollY - scrollOffset.current;
            if (diff)
                window.scrollTo({ top: diff });
        });
        return () => customEventBus.off('header:resetprogress', () => {
            const diff = window.scrollY - scrollOffset.current;
            if (diff)
                window.scrollTo({ top: diff });
        });
    }, []);
    // ANCHOR - TRANSPARENCY ANIMATION ------------------------------------------
    useLayoutEffect(() => {
        if (!!hero) {
            ScrollTrigger.create({
                id: 'header-transparency',
                trigger: hero,
                markers: markers && {
                    indent: 200,
                },
                start: 'bottom top',
                end: 'bottom top',
                onEnter: () => {
                    customEventBus.emit('header:settrasparency', { transparent: false, color: 'black' });
                },
                onEnterBack: () => {
                    customEventBus.emit('header:settrasparency', { transparent: true, color: 'white' });
                },
            });
        }
        return () => {
            var _a;
            customEventBus.emit('header:settransparency', {
                transparent: false,
                color: 'black',
            });
            (_a = ScrollTrigger.getById('header-transparency')) === null || _a === void 0 ? void 0 : _a.kill();
        };
    }, [hero]);
    return ref;
}
export default useHeaderRef;
